import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { ArrowRight, Car, AlertCircle, CheckCircle } from "lucide-react";
import { useFlow } from "../../hooks/useFlow";
import ExtendedWarrantyScreenWrapper, {
  ExtendedWarrantyHeader,
  ExtendedWarrantyCard,
  ExtendedWarrantyButton,
} from "./ExtendedWarrantyScreenWrapper";
import { useAuth } from "../../hooks/useAuth";
import { buildFlowUrl } from "../../utils/FlowUrlBuilder";
import analyticsService from "../../lib/AnalyticsService";

const QualificationScreen = ({ onComplete, onExit }) => {
  useAuth();

  useEffect(() => {
    analyticsService.trackEvent("Extended Warranty Page View", {
      page: "ew_qualification",
      screen: "qualification",
      flow_type: "extended-warranty",
    });
  }, []);

  const [carAge, setCarAge] = useState(() => {
    const cached = sessionStorage.getItem("warrantyQualification");
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        return parsed.carAge?.toString() || "";
      } catch (e) {
        return "";
      }
    }
    return "";
  });

  const [showResult, setShowResult] = useState(false);
  const [qualifies, setQualifies] = useState(false);
  const { getConfig } = useFlow();

  // Get minimum car age from config
  const minCarAge = getConfig("warranty.minCarAge") || 4;

  const handleAgeSubmit = () => {
    const age = parseInt(carAge);
    const doesQualify = age >= minCarAge;

    analyticsService.trackEvent("Extended Warranty Qualification Check", {
      car_age: age,
      qualifies: doesQualify,
      min_car_age: minCarAge,
    });

    setQualifies(doesQualify);
    setShowResult(true);

    // Store qualification result
    sessionStorage.setItem(
      "warrantyQualification",
      JSON.stringify({
        carAge: age,
        qualifies: doesQualify,
        timestamp: new Date().toISOString(),
      }),
    );
  };

  const handleContinue = async () => {
    console.log("handle continue");
    if (qualifies) {
      console.log("qualifies");
      onComplete({ qualifies: true, carAge: parseInt(carAge) });
    } else {
      onExit({ reason: "does_not_qualify", carAge: parseInt(carAge) });
    }
  };

  const handleTryAgain = () => {
    setShowResult(false);
    setCarAge("");
  };

  if (showResult) {
    return (
      <div className="space-y-8 p-4 pt-5 pb-6">
        <div className="text-center bg-white p-5 rounded-[20px] space-y-4">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${
              qualifies ? "bg-green-100" : "bg-red-100"
            }`}
          >
            {qualifies ? (
              <CheckCircle className="w-8 h-8 text-green-500" />
            ) : (
              <AlertCircle className="w-8 h-8 text-red-500" />
            )}
          </div>

          <h1 className="text-3xl font-bold text-gray-900">
            {qualifies ? "Great News!" : "Not Quite Ready"}
          </h1>

          <p className="text-lg text-gray-600">
            {qualifies
              ? `Your ${carAge}-year-old vehicle qualifies for extended warranty coverage!`
              : `Extended warranties are typically available for vehicles ${minCarAge} years or older. Your ${carAge}-year-old vehicle may still be under manufacturer warranty.`}
          </p>
        </div>

        <Card
          className={`border-none rounded-[20px] ${
            qualifies ? "bg-greenish" : "bg-red-50"
          }`}
        >
          <CardContent className="p-5">
            {qualifies ? (
              <div className="text-center">
                <p className="text-darkGrey font-medium mb-2">
                  You're eligible to continue!
                </p>
                <p className="text-sm text-darkGrey">
                  Let's help you find the right extended warranty coverage for
                  your vehicle.
                </p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-red-700 font-medium mb-2">
                  Extended warranty may not be necessary
                </p>
                <p className="text-sm text-red-600">
                  Your vehicle likely still has manufacturer warranty coverage.
                  Consider checking back when your car is older than {minCarAge}{" "}
                  years.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-3">
          {qualifies ? (
            <ExtendedWarrantyButton onClick={() => {
              analyticsService.trackEvent("Extended Warranty Button Click", {
                button_name: "qualification-continue",
                screen: "qualification",
                car_age: parseInt(carAge),
              });
              handleContinue();
            }} className="px-10">
              Continue
              <ArrowRight className="w-4 h-4" />
            </ExtendedWarrantyButton>
          ) : (
            <>
              <p className="text-lg text-gray-600 text-center mb-2">
                Check out other ways to save:
              </p>
              <ExtendedWarrantyButton
                onClick={() => {
                  analyticsService.trackEvent("Extended Warranty Flow Exit", {
                    exit_point: "qualification_disqualified",
                    destination: "insurance_flow",
                    flow_type: "extended-warranty",
                    car_age: parseInt(carAge),
                  });
                  window.location.href = buildFlowUrl("insurance");
                }}
                className="!bg-purpleGradient hover:!bg-purpleGradientDark"
              >
                Insurance Offers
              </ExtendedWarrantyButton>
              <div className="text-center">
                <a
                  href="https://www.ownli.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 underline inline-flex items-center text-sm"
                  onClick={() => {
                    analyticsService.trackEvent("Extended Warranty Flow Exit", {
                      exit_point: "qualification_disqualified",
                      destination: "ownli_app",
                      flow_type: "extended-warranty",
                      car_age: parseInt(carAge),
                    });
                  }}
                >
                  Other offers at Ownli
                </a>
              </div>
            </>
          )}

          {qualifies && (
            <button
              onClick={() => {
                analyticsService.trackEvent("Extended Warranty Button Click", {
                  button_name: "try-different-age",
                  screen: "qualification",
                  previous_age: parseInt(carAge),
                });
                handleTryAgain();
              }}
              className="w-full text-center text-gray-600 hover:text-gray-900 text-sm"
            >
              Try a different vehicle age
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <ExtendedWarrantyScreenWrapper>
      <ExtendedWarrantyHeader
        icon={<Car className="w-8 h-8 text-blue-500" />}
        title="Let's Check Your Vehicle's Eligibility"
        subtitle={`Extended warranty coverage is typically available for vehicles ${minCarAge} years or older.`}
      />

      <ExtendedWarrantyCard>
        <div className="space-y-6">
          <div className="space-y-3">
            <label
              htmlFor="carAge"
              className="block text-lg font-medium text-gray-900"
            >
              How old is your car?
            </label>
            <p className="text-sm text-gray-600">
              Enter the age of your vehicle in years (based on model year).
            </p>
          </div>

          <div className="space-y-4">
            <div className="relative">
              <input
                id="carAge"
                type="number"
                min="1"
                max="30"
                value={carAge}
                onChange={(e) => setCarAge(e.target.value)}
                placeholder="Enter vehicle age (e.g., 5)"
                className="w-full p-4 pr-16 text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-transparent"
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">
                years
              </div>
            </div>

            <ExtendedWarrantyButton
              onClick={handleAgeSubmit}
              disabled={!carAge || parseInt(carAge) < 1}
            >
              Check Eligibility
              <ArrowRight className="w-4 h-4" />
            </ExtendedWarrantyButton>
          </div>
        </div>
      </ExtendedWarrantyCard>

      <ExtendedWarrantyCard variant="info">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-green flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-darkGrey">
              Why Vehicle Age Matters
            </p>
            <p className="text-xs text-darkGrey">
              Extended warranties are designed to provide coverage after your
              manufacturer's warranty expires. Newer vehicles are typically
              still covered under factory warranty.
            </p>
          </div>
        </div>
      </ExtendedWarrantyCard>
    </ExtendedWarrantyScreenWrapper>
  );
};

export default QualificationScreen;
